function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useState, useEffect } from "react";
import { useClassPrepend } from "../../../../hook/ui/common/className/useClassPrepend";
import { useChildrenProps } from "../../../../hook/ui/common/children/useChildrenProps";

/*
    Wraps the Table component to add sorting

    props
        (array, required)   columns               This component uses the following keys from each column object (if they are declared)
                                                    - (boolean, optional)  sortEnabled    When true, the column can be sorted and the sorting controls are shown
                                                    - (function, optional) sortValueFunc  A function that returns the value to sort by
                                                        - Example where the cell (row dataKey value) is an object: (cell, row) => cell.name
        (object, optional)  defaultSort           The initial filter to apply to the table
                                                    - Example = { 
                                                        dataKey: "name", 
                                                        order: "ASC" // or "DESC"
                                                    }
        (object, optional)  sortButtonContentMap  Object of keys and their jsx values for the sort button controls
*/

const UnstyledTableSortWrapper = ({
  classPrepend = "unstyled",
  columns = [],
  rows = [],
  defaultSort = {},
  sortButtonContentMap = {
    ascending: "asc",
    descending: "desc"
  },
  children,
  ...rest
}) => {
  const [getClassName, getClassNameWithGlobal] = useClassPrepend(classPrepend);
  const [activeSort, setActiveSort] = useState(defaultSort);
  const [sortedRows, setSortedRows] = useState(rows);
  const handleSetActiveSort = (dataKey, order) => {
    const newActiveSort = {
      dataKey,
      order: order.toUpperCase()
    };
    if (activeSort.dataKey === newActiveSort.dataKey && activeSort.order === newActiveSort.order) {
      clearActiveSort();
      return;
    }
    setActiveSort(newActiveSort);
  };
  useEffect(() => {
    if (activeSort.dataKey) {
      applyActiveSort();
      return;
    }
    setSortedRows(rows);
  }, [rows, activeSort]);
  const applyActiveSort = () => {
    const {
      dataKey,
      order
    } = activeSort;
    if (!dataKey || !order) return;
    const sortColumnIndex = columns.findIndex(column => column.dataKey === dataKey);
    if (sortColumnIndex === -1) return;
    const sortedColumn = columns[sortColumnIndex];
    if (!sortedColumn.sortEnabled) return;
    let sortValueFunc = (cell, row) => cell;
    if (sortedColumn.sortValueFunc) {
      sortValueFunc = sortedColumn.sortValueFunc;
    }
    let newSortedRows = structuredClone(rows);
    newSortedRows = newSortedRows.sort((a, b) => {
      a = sortValueFunc(a[dataKey], a);
      b = sortValueFunc(b[dataKey], b);
      return a < b ? -1 : a > b ? 1 : 0;
    });
    if (order.toUpperCase() === "DESC") {
      newSortedRows = newSortedRows.reverse();
    }
    setSortedRows(newSortedRows);
  };
  const clearActiveSort = () => {
    setActiveSort({});
    setSortedRows(rows);
  };
  const sortableColumns = columns.map((column, index) => {
    const newHeaderCellContentRight = /*#__PURE__*/React.createElement(React.Fragment, null, column.headerCellContentRight && column.headerCellContentRight, column.sortEnabled ? /*#__PURE__*/React.createElement("div", getClassName("table-header-cell-sort"), /*#__PURE__*/React.createElement("button", _extends({}, getClassName(activeSort?.dataKey === column.dataKey && activeSort?.order.toUpperCase() === "ASC" ? "table-header-cell-sort-active" : ""), {
      onClick: () => handleSetActiveSort(column.dataKey, "ASC")
    }), sortButtonContentMap.ascending), /*#__PURE__*/React.createElement("button", _extends({}, getClassName(activeSort?.dataKey === column.dataKey && activeSort?.order.toUpperCase() === "DESC" ? "table-header-cell-sort-active" : ""), {
      onClick: () => handleSetActiveSort(column.dataKey, "DESC")
    }), sortButtonContentMap.descending)) : null);
    return {
      ...column,
      headerCellContentRight: newHeaderCellContentRight
    };
  });
  const [childrenWithProps] = useChildrenProps(children, {
    classPrepend,
    columns: sortableColumns,
    rows: sortedRows,
    ...rest
  });
  return childrenWithProps;
};
export { UnstyledTableSortWrapper };